import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Icon,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { Facility } from '@schooly/api';
import { getChildrenByParentId, useFacilities } from '@schooly/hooks/use-facilities';
import { ArrowAngleIcon, EditIcon, GridRowItem, GridRowName, GridRowStyled } from '@schooly/style';
import { formatList } from '@schooly/utils/common';
import { FC, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { Counter } from '../../../../components/uikit/Counter/Counter.styled';
import { SimpleButton } from '../../../../components/uikit/SimpleButton/SimpleButton';
import { GridContainer } from '../../../../components/uikit-components/Grid/Grid';
import { useSchool } from '../../../../hooks/useSchool';

export const SchoolGeneralFacilities: FC = () => {
  const { schoolId = '' } = useSchool();
  const { formatMessage } = useIntl();
  const [expanded, setExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const roomsTextRef = useRef<HTMLSpanElement>(null);

  const { counts, campuses, standaloneBuildings, standaloneRooms, childrenByParentId, isLoading } =
    useFacilities({ schoolId }, { enabled: !!schoolId });

  const hasOnlyRooms = counts.campus === 0 && counts.building === 0 && counts.room > 0;
  const canExpand = !isLoading && (!hasOnlyRooms || isTruncated);
  const standAloneRoomsList = useMemo(() => formatList(standaloneRooms), [standaloneRooms]);

  const toggleExpand = useCallback(() => {
    setExpanded((expanded) => !expanded);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: standAloneRoomsList should be in dependencies list
  useLayoutEffect(() => {
    const el = roomsTextRef.current;
    if (el && !expanded) {
      setIsTruncated(el.scrollHeight > el.clientHeight);
    }
  }, [standAloneRoomsList, expanded]);

  const counter = useMemo(() => {
    if (isLoading) {
      return (
        <Skeleton
          variant="circular"
          sx={(theme) => ({
            ml: 1,
            width: theme.spacing(2.5),
            height: theme.spacing(2.5),
          })}
        />
      );
    }
    if (counts.room > 0) {
      return <Counter>{counts.room}</Counter>;
    }
    return null;
  }, [counts.room, isLoading]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <Stack spacing={1} sx={{ width: '100%' }}>
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="40%" />
          <Skeleton variant="text" width="50%" />
        </Stack>
      );
    }

    if (hasOnlyRooms) {
      return (
        <Typography
          ref={roomsTextRef}
          variant="h3"
          color="text.primary"
          sx={{
            ...(!expanded && {
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }),
          }}
        >
          {standAloneRoomsList}
        </Typography>
      );
    }

    if (expanded) {
      return (
        <Stack gap={2.5} width="100%">
          {campuses.map((campus) => (
            <FacilitySection
              key={campus.id}
              campus={campus}
              buildings={getChildrenByParentId(childrenByParentId, campus.id)}
              childrenByParentId={childrenByParentId}
            />
          ))}

          {standaloneBuildings.map((building) => (
            <FacilitySection
              key={building.id}
              buildings={[building]}
              childrenByParentId={childrenByParentId}
            />
          ))}

          {!!standAloneRoomsList && (
            <Typography variant="h3" color="text.primary" p={1}>
              {standAloneRoomsList}
            </Typography>
          )}
        </Stack>
      );
    }

    return (
      <Stack>
        {counts.campus > 0 && (
          <Typography variant="h3" color="text.primary">
            {formatMessage({ id: 'school-tabs-Facilities-Campuses' })}: {counts.campus}
          </Typography>
        )}
        {counts.building > 0 && (
          <Typography variant="h3" color="text.primary">
            {formatMessage({ id: 'school-tabs-Facilities-Buildings' })}: {counts.building}
          </Typography>
        )}
        {counts.room > 0 && (
          <Typography variant="h3" color="text.primary">
            {formatMessage({ id: 'school-tabs-Facilities-Rooms' })}: {counts.room}
          </Typography>
        )}
      </Stack>
    );
  };

  return (
    <Card component={Stack} sx={{ minHeight: 224 }}>
      <CardHeader
        title={
          <Stack direction="row" alignItems="center">
            <FormattedMessage id="school-tabs-Facilities" />
            {counter}
          </Stack>
        }
        action={
          <Link to="/settings/facilities">
            <IconButton inverse>
              <EditIcon />
            </IconButton>
          </Link>
        }
      />
      <CardContent
        component={Stack}
        sx={{ flex: '1', justifyContent: 'space-between', alignItems: 'flex-start' }}
      >
        <>
          {renderContent()}

          {canExpand && (
            <SimpleButton sx={{ mt: 2.5 }} onClick={toggleExpand}>
              {expanded ? (
                <FormattedMessage id="action-Hide" />
              ) : (
                <FormattedMessage id="action-ViewAll" />
              )}
            </SimpleButton>
          )}
        </>
      </CardContent>
    </Card>
  );
};

interface FacilityCampusSectionProps {
  campus?: Facility;
  buildings: Facility[];
  childrenByParentId: Record<string, Facility[]>;
}

const FacilitySection: FC<FacilityCampusSectionProps> = ({
  campus,
  buildings,
  childrenByParentId,
}) => {
  const borderBottom = !!buildings.length;

  return (
    <Box>
      {campus && (
        <Typography variant="h3" color="text.primary" p={1}>
          {campus.name}
        </Typography>
      )}
      <GridContainer
        sx={(theme) => ({
          borderBottom: borderBottom ? theme.mixins.borderValue() : undefined,
          borderRadius: 0,
        })}
      >
        {buildings.map((building) => {
          const rooms = childrenByParentId[building.id] ?? [];

          return (
            <GridRowStyled key={building.id} noBorderRadius>
              <GridRowItem sx={{ justifyContent: 'space-between', gap: 1 }}>
                <Stack direction="row" alignItems="center" gap={1} maxWidth="50%">
                  {campus && (
                    <Icon sx={{ color: 'common.grey4' }}>
                      <ArrowAngleIcon />
                    </Icon>
                  )}
                  <GridRowName sx={{ whiteSpace: 'wrap' }}>{building.name}</GridRowName>
                </Stack>
                <GridRowName maxWidth="50%" sx={{ whiteSpace: 'wrap' }}>
                  {rooms.length > 0 ? formatList(rooms) : '-'}
                </GridRowName>
              </GridRowItem>
            </GridRowStyled>
          );
        })}
      </GridContainer>
    </Box>
  );
};
